/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState } from 'react';
import SelectBox from '@components/form/SelectBox';

import useApi from '@/hooks/useApi';
import { MsAccount } from '@api/ms-account';
import PropTypes from 'prop-types';

const propTypes = {
  testID: PropTypes.string,
  editable: PropTypes.bool,
  isRequired: PropTypes.bool,
  hideLabel: PropTypes.bool,
  multiple: PropTypes.bool,
  isGroup: PropTypes.bool,
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  customFilter: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  notFetch: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
  isAll: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  labelKey: PropTypes.string,
  useCallbackOptions: PropTypes.array,
  customSort: PropTypes.object,
  limit: PropTypes.number,
};

const defaultProps = {
  testID: 'sales-person-search',
  editable: true,
  hideLabel: false,
  isRequired: false,
  multiple: false,
  isGroup: false,
  hasError: false,
  onChange: () => {},
  notFetch: false,
  customFilter: {},
  value: '',
  isAll: false,
  label: 'Sales Person',
  placeholder: 'Select Sales Person',
  labelKey: 'name',
  useCallbackOptions: [],
  customSort: {},
  limit: 10,
};

const SalesPersonSearch = (props) => {
  const {
    editable = true,
    hideLabel = false,
    isRequired = false,
    multiple = false,
    isGroup = false,
    hasError = false,
    customFilter = {},
    notFetch = false,
    value = '',
    isAll = false,
    label = 'Sales Person',
    placeholder = 'Select Sales Person',
    labelKey = 'name',
    useCallbackOptions,
    customSort = {},
    limit = 10,
  } = props;

  const [loading, setLoading] = useState({
    initial: false,
    loadMore: false,
  });
  const [options, setOptions] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);

  const getUsers = useApi(MsAccount.getUsers);

  const onSearch = async (val, newSkip = 0) => {
    if (newSkip) {
      setLoading((prev) => ({ ...prev, loadMore: true }));
    } else {
      setLoading((prev) => ({ ...prev, initial: true }));
    }
    try {
      let filter = {
        'role.code': 'salesman',
        sales_code: { $exists: true },
      };
      if (customFilter && Object.keys(customFilter).length) {
        if (customFilter?.remove_default_filter) {
          const newCustomFilter = JSON.parse(JSON.stringify(customFilter));
          delete newCustomFilter.remove_default_filter;
          filter = newCustomFilter;
        } else {
          filter = {
            ...filter,
            ...customFilter,
          };
        }
      }
      if (val) {
        filter[labelKey] = { $regex: val, $options: 'i' };
      } else if (!val && filter[labelKey]) {
        delete filter[labelKey];
      }
      const params = {
        filter,
        skip: newSkip,
        limit,
      };
      if (
        customSort &&
        typeof customSort === 'object' &&
        Object.keys(customSort).length
      ) {
        params.sort = JSON.stringify(customSort);
      }
      const response = await getUsers.request({ params });
      if (response.status === 200 && response?.data?.data?.length) {
        const result = response.data.data.map((item) => {
          if (item._id) {
            item.id = item._id;
          }
          return item;
        });
        setHasMoreData(result.length >= limit);
        const convertValue = (values) => {
          if (isGroup) {
            const branchs = val ? [] : options;
            for (let i = 0; i < values.length; i++) {
              const item = values[i];
              if (item?.branch?.length) {
                for (let j = 0; j < item.branch.length; j++) {
                  const child = item.branch[j];
                  const index = branchs.findIndex((b) => b.id == child.id);
                  if (child?.id && index === -1) {
                    branchs.push({
                      ...child,
                      name: child.name?.toUpperCase(),
                      data: [],
                    });
                  }
                }
              }
            }
            if (branchs?.length) {
              for (let i = 0; i < values.length; i++) {
                const item = values[i];
                if (item?.branch?.length) {
                  for (let j = 0; j < item.branch.length; j++) {
                    const child = item.branch[j];
                    const branchIndex = branchs?.findIndex(
                      (b) => child.id == b.id,
                    );
                    if (branchIndex > -1) {
                      const index = branchs[branchIndex].data?.findIndex(
                        (d) => d.id == item.id,
                      );
                      if (index === -1) {
                        branchs[branchIndex].data.push(item);
                      }
                    }
                  }
                }
              }
            }
            return branchs;
          }
          return values;
        };
        if (newSkip) {
          setOptions(convertValue(options.concat(result)));
        } else {
          if (isAll) {
            result.unshift({ id: 'all', name: 'All' });
          }
          setOptions(convertValue(result));
        }
      } else {
        if (!newSkip) {
          setOptions([]);
        }
        setHasMoreData(false);
      }

      if (newSkip) {
        setLoading((prev) => ({ ...prev, loadMore: false }));
      } else {
        setLoading((prev) => ({ ...prev, initial: false }));
      }
    } catch (error) {
      if (newSkip) {
        setLoading((prev) => ({ ...prev, loadMore: false }));
      } else {
        setLoading((prev) => ({ ...prev, initial: false }));
      }
      setHasMoreData(false);
    }
  };

  return (
    <SelectBox
      {...props}
      label={label}
      options={options}
      value={value}
      placeholder={
        loading.initial || loading.loadMore ? 'Loading...' : placeholder
      }
      trackBy="id"
      labelKey={labelKey}
      isRequired={isRequired}
      hideLabel={hideLabel}
      multiple={multiple}
      isGroup={isGroup}
      editable={editable && !loading.initial}
      hasError={hasError}
      loading={loading.initial}
      loadingCallback={loading.loadMore}
      withCallback={true}
      withLoadMore={true}
      loadMoreEmpty={!hasMoreData}
      useCallbackOptions={useCallbackOptions}
      searchCallback={(val) => {
        setSkip(0);
        onSearch(val, 0);
      }}
      onLoadMore={(val) => {
        const newSkip = skip + limit;
        setSkip(newSkip);
        onSearch(val, newSkip);
      }}
      onChange={(val) => {
        if (isAll) {
          const allValue = { id: 'all', name: 'All' };
          if (multiple) {
            if (val?.length) {
              const idx = val.findIndex((c) => c.id == 'all');
              if (idx > -1 && val?.length > 1) {
                const childIdx = value?.findIndex((c) => c.id == 'all');
                if (childIdx > -1) {
                  val.splice(idx, 1);
                } else {
                  val = [allValue];
                }
              }
            }
          } else {
            if (!val?.id) {
              val = allValue;
            }
          }
        }
        props.onChange(val);
      }}
      onReady={() => {
        if (!notFetch) {
          setSkip(0);
          onSearch(undefined, 0);
        }
      }}
      forceLoadMore={isGroup ? hasMoreData : false}
    />
  );
};

SalesPersonSearch.propTypes = propTypes;
SalesPersonSearch.defaultProps = defaultProps;

export default SalesPersonSearch;
